import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { api, formatGBP } from '@/lib/api';
import useSEO from '@/lib/useSEO';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Check, Clock } from 'lucide-react';

export default function OrderConfirmation() {
    const location = useLocation();
    useSEO({ title: 'Order confirmation', description: 'Your order with Leiha Martin Art.', path: '/order/confirmation', noindex: true });
    const [state, setState] = useState({ loading: true, paid: false, error: null });
    const [order, setOrder] = useState(null);
    const attemptsRef = useRef(0);

    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const orderId = params.get('order_id');

    useEffect(() => {
        if (!sessionId) {
            setState({ loading: false, paid: false, error: 'Missing payment session.' });
            return;
        }
        let cancelled = false;
        const poll = async () => {
            attemptsRef.current += 1;
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                if (r.data.payment_status === 'paid') {
                    setState({ loading: false, paid: true, error: null });
                    if (orderId) {
                        try {
                            const o = await api.get(`/orders/${orderId}`);
                            if (!cancelled) setOrder(o.data);
                        } catch (_) {}
                    }
                    return;
                }
                if (r.data.status === 'expired') {
                    setState({ loading: false, paid: false, error: 'Your payment session expired.' });
                    return;
                }
                if (attemptsRef.current >= 8) {
                    setState({ loading: false, paid: false, error: 'Still waiting on confirmation. Check back in a moment.' });
                    return;
                }
                setTimeout(poll, 2000);
            } catch (e) {
                if (cancelled) return;
                if (attemptsRef.current >= 5) {
                    setState({ loading: false, paid: false, error: 'Unable to verify payment status.' });
                    return;
                }
                setTimeout(poll, 2500);
            }
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div className="py-16 sm:py-20">
            <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8">
                <p className="caption mb-3">Order</p>
                <h1 className="font-display text-4xl sm:text-5xl text-[color:var(--umber)]">Confirmation</h1>

                <div className="mt-8 rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper-2)] p-6" data-testid="order-confirmation-status">
                    {state.loading && (
                        <div className="flex items-center gap-3" data-testid="loading-state">
                            <Clock className="h-5 w-5 text-[color:var(--graphite)]" />
                            <div className="flex-1">
                                <p className="text-sm text-[color:var(--umber)]">Checking payment status…</p>
                                <Skeleton className="h-3 w-2/3 mt-2 bg-[color:var(--paper)]" />
                            </div>
                        </div>
                    )}
                    {!state.loading && state.paid && (
                        <div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[rgba(47,125,90,0.14)] flex items-center justify-center">
                                    <Check className="h-5 w-5 text-[color:var(--success,#2F7D5A)]" />
                                </div>
                                <div>
                                    <p className="font-display text-xl text-[color:var(--umber)]">Thank you — payment received.</p>
                                    <p className="text-sm text-[color:var(--muted-fg)]">Your commission is now with Leiha. You'll hear back by email shortly.</p>
                                </div>
                            </div>
                            {order && (
                                <div className="mt-6 border-t border-[color:var(--border)] pt-6 text-sm space-y-2">
                                    <div className="flex justify-between"><span className="text-[color:var(--graphite)]">Order reference</span><span className="text-[color:var(--umber)] font-mono">{order.id.slice(0, 8).toUpperCase()}</span></div>
                                    <div className="flex justify-between"><span className="text-[color:var(--graphite)]">Total paid</span><span className="text-[color:var(--umber)]">{formatGBP(order.total)}</span></div>
                                    {order.image_count !== undefined && (
                                        <div className="flex justify-between"><span className="text-[color:var(--graphite)]">Reference photos</span><span className="text-[color:var(--umber)]">{order.image_count}</span></div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {!state.loading && !state.paid && state.error && (
                        <Alert className="border-[color:var(--border)] bg-[color:var(--paper)]" data-testid="error-state">
                            <AlertDescription className="text-sm text-[color:var(--umber)]">
                                {state.error}
                                <div className="mt-3">
                                    <Button
                                        onClick={() => window.location.reload()}
                                        data-testid="order-confirmation-retry-button"
                                        className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]"
                                    >
                                        Check again
                                    </Button>
                                </div>
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <div className="mt-10 flex items-center justify-between">
                    <Link to="/" className="under-link text-sm text-[color:var(--graphite)]">Back to home</Link>
                    <Link to="/gallery" className="under-link text-sm text-[color:var(--graphite)]">View gallery →</Link>
                </div>
            </div>
        </div>
    );
}
